import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Clock, BookMarked } from "lucide-react";
import BookCover from "./BookCover";
import PrintInterestButton from "./PrintInterestButton";
import ReleaseInterestButton from "./ReleaseInterestButton";

/**
 * Equal-structure collection card used in the "Explore the LifeIQ
 * Library" grid (Command 02 §6/§12). Every series — released or
 * "coming soon" — renders through this same component so no collection
 * looks structurally bigger or smaller than another; only its accent
 * colours and content differ.
 */
export default function CollectionCard({
  testId,
  name,
  levelNumber,
  descriptor,
  languageBadge,
  covers = [],
  format,
  availability = "available", // "available" | "prepared" | "coming_soon"
  stat,
  priceIndicator,
  badges = [],
  accent,
  primaryHref,
  primaryLabel,
  printInterest,
  releaseInterest,
}) {
  const availabilityMeta = {
    available: { icon: CheckCircle2, text: "Digital Editions Available", color: accent.primary },
    available_print: { icon: CheckCircle2, text: "Printed Editions Available", color: accent.primary },
    prepared: { icon: CheckCircle2, text: "Digital Series Prepared", color: accent.primary },
    coming_soon: { icon: Clock, text: "Coming Soon", color: accent.text },
  }[availability];
  const AvailabilityIcon = availabilityMeta.icon;

  return (
    <div
      data-testid={testId}
      className="rounded-sm border h-full flex flex-col overflow-hidden transition-transform duration-200 hover:-translate-y-1"
      style={{ backgroundColor: accent.bg, borderColor: accent.border, color: accent.text }}
    >
      <div className="h-1.5 w-full" style={{ backgroundColor: accent.primary }} />
      <div className="p-6 sm:p-7 flex flex-col flex-1">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.12em]" style={{ color: availabilityMeta.color }}>
          <AvailabilityIcon size={13} /> {availabilityMeta.text}
        </div>

        <h3 className="mt-3 font-serif-d text-[22px] sm:text-[24px] leading-[1.15]">{name}</h3>

        {languageBadge && (
          <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.1em] px-2.5 py-1 rounded-sm self-start" style={{ backgroundColor: `${accent.primary}1A`, color: accent.primary }}>
            {languageBadge}
          </div>
        )}

        <p className="mt-3 text-[14px] leading-[1.6]" style={{ color: accent.textMuted || accent.text, opacity: 0.85 }}>
          {descriptor}
        </p>

        {badges.length > 0 && (
          <div className="mt-3 flex items-center gap-1.5 flex-wrap">
            {badges.map((b, i) => (
              <span key={b} className="flex items-center gap-1.5">
                <span className="text-[10px] font-bold uppercase tracking-[0.08em] px-2 py-1 rounded-sm border" style={{ borderColor: `${accent.primary}55`, color: accent.primary }}>
                  {b}
                </span>
                {i < badges.length - 1 && <ArrowRight size={10} style={{ opacity: 0.35 }} />}
              </span>
            ))}
          </div>
        )}

        {covers.length > 0 ? (
          <div className="mt-5 grid grid-cols-3 gap-2.5 max-w-[260px]">
            {covers.slice(0, 3).map((b) => (
              <BookCover key={b.slug} book={b} testId={`${testId}-cover-${b.slug}`} />
            ))}
          </div>
        ) : (
          <div
            data-testid={`${testId}-coming-soon-placeholder`}
            className="mt-5 h-20 rounded-sm border flex flex-col items-center justify-center gap-0.5"
            style={{ borderColor: `${accent.primary}35`, backgroundColor: `${accent.primary}0D` }}
          >
            <div className="flex items-center gap-1.5" style={{ color: accent.primary }}>
              <BookMarked size={13} />
              {levelNumber && <span className="text-[13px] font-serif-d font-bold">L{levelNumber}</span>}
            </div>
            <span className="text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: `${accent.text}88` }}>Cover Coming Soon</span>
          </div>
        )}

        <div className="mt-5 flex items-center gap-2 text-[12px] uppercase tracking-[0.12em] font-semibold" style={{ opacity: 0.75 }}>
          {stat && <span>{stat}</span>}
          {stat && format && <span style={{ color: accent.primary }}>·</span>}
          {format && <span>{format}</span>}
        </div>
        {priceIndicator && (
          <div data-testid={`${testId}-price-indicator`} className="mt-2 text-[13px] font-bold" style={{ color: accent.primary }}>
            {priceIndicator}
          </div>
        )}

        <div className="mt-auto pt-6 flex flex-wrap items-center gap-x-5 gap-y-3">
          {primaryHref && (
            <Link
              to={primaryHref}
              data-testid={`${testId}-primary-cta`}
              className="inline-flex items-center gap-2 px-6 py-3 text-[12px] font-semibold tracking-wide uppercase rounded-sm text-white hover:opacity-90 transition-opacity duration-200"
              style={{ backgroundColor: accent.primary }}
            >
              {primaryLabel} <ArrowRight size={13} />
            </Link>
          )}
          {releaseInterest && (
            <ReleaseInterestButton
              seriesId={releaseInterest.seriesId}
              seriesName={releaseInterest.seriesName}
              sourcePage={releaseInterest.sourcePage}
              label={releaseInterest.label || `Tell Me When Level ${levelNumber} Is Ready →`}
              accentColor={accent.primary}
              testId={`${testId}-release-interest`}
              className="inline-flex items-center gap-2 px-6 py-3 text-[12px] font-semibold tracking-wide uppercase rounded-sm text-white hover:opacity-90 transition-opacity duration-200"
              style={{ backgroundColor: accent.primary }}
            />
          )}
          {printInterest && (
            <PrintInterestButton
              mode="single"
              seriesId={printInterest.seriesId}
              sourcePage={printInterest.sourcePage}
              label={printInterest.label || "I'm Interested in Print →"}
              accentColor={accent.primary}
              testId={`${testId}-print-interest`}
              className="text-[12px] font-semibold uppercase tracking-wide underline underline-offset-2 hover:opacity-70 transition-opacity"
            />
          )}
        </div>
      </div>
    </div>
  );
}

